import { Injectable } from '@angular/core';
import { ComponentType, IPxlEvent, PxlEvent } from '@pxl/models/common';
import { PxlApplicationStateService } from '@pxl/models/contracts';
import { Observable, Subject, Subscription } from 'rxjs';

@Injectable()
export class DefaultPxlApplicationStateService implements PxlApplicationStateService {
    private readonly events: Subject<IPxlEvent>;
    private readonly globalState: Map<string, unknown>;
    private readonly eventMap: Map<string, Subject<unknown>>;

    public constructor() {
        this.events = new Subject<IPxlEvent>();
        this.globalState = new Map<string, unknown>();
        this.eventMap = new Map<string, Subject<unknown>>();
    }

    public getObservableEvent<TEvent, TEventArgs>(eventType: ComponentType<TEvent, TEventArgs>): Observable<TEvent> {
        const eventTypeName = eventType.getType();
        let eventObservable: Subject<TEvent>;
        if (this.eventMap.has(eventTypeName)) eventObservable = this.eventMap.get(eventTypeName) as Subject<TEvent>;
        else {
            eventObservable = new Subject<TEvent>();
            this.eventMap.set(eventTypeName, eventObservable as Subject<unknown>);
        }
        return eventObservable;
    }

    public subscribeToEvent<TEvent, TEventArgs>(
        eventType: ComponentType<TEvent, TEventArgs>,
        next: (pxlEvent: TEvent) => void,
    ): Subscription {
        const eventTypeName = eventType.getType();
        const eventObservable = this.getObservableEvent(eventType);
        const sub = eventObservable.subscribe(next);

        return new Subscription(() => {
            sub.unsubscribe();
            if (this.eventMap.get(eventTypeName) && !this.eventMap.get(eventTypeName).observed) {
                this.eventMap.delete(eventTypeName);
            }
        });
    }

    public raiseEventSpecific<TEvent extends PxlEvent<TEventArgs>, TEventArgs>(pxlEvent: TEvent) {
        const eventType = pxlEvent.getType();
        if (this.eventMap.has(eventType)) {
            this.eventMap.get(eventType)?.next(pxlEvent);
        }
    }

    // subscribe to events
    public subscribeToEvents(next: (event: IPxlEvent) => void): Subscription {
        return this.events.subscribe(next);
    }

    public raiseEvent(eventType: string, eventArgs?: unknown) {
        this.events.next({
            eventType,
            eventArgs,
        });
    }

    // use a library?
    public registerToState(component: string, state: unknown) {
        this.globalState.set(component, state);

        return {
            unregister: (() => {
                this.unregisterFromState(component);
            }).bind(this),
        };
    }

    // still requires a lot of work
    // register to global state?
    // component passes it self?

    public retrieveComponentState(component: string): unknown {
        return this.globalState.get(component);
    }

    public unregisterFromState(component: string): void {
        this.globalState.delete(component);
    }
}
